import { Injectable, inject, signal } from '@angular/core';
import { ApiService } from 'services/api.service';
import type { InformationRequestLabelsResponse, Language, NameDisplayMode, NameDisplayRow } from 'types/information-request.interface';

/**
 * LNA-1927: the information request form field/section captions are owned by the backend
 * (single source of truth — the BO PDF renders from the same values). This service fetches
 * them once, caches them, and exposes a typed lookup for the IR view/summary/edit components.
 *
 * Lookups return '' when labels are not (yet) loaded or the endpoint failed, so the UI never
 * crashes; captions simply render empty until the data arrives.
 */
@Injectable({ providedIn: 'root' })
export class IrFormLabelsService {
  private readonly api = inject(ApiService);

  private readonly labelsSignal = signal<InformationRequestLabelsResponse | null>(null);
  private loadingLanguage: Language | null = null;

  /** Idempotent: triggers a single fetch per language; safe to call from every IR component's ngOnInit. */
  ensureLoaded(language: Language = 'Lv'): void {
    if (this.labelsSignal() || this.loadingLanguage === language) return;
    this.loadingLanguage = language;
    this.api.getInformationRequestFormLabels(language).subscribe({
      next: (data) => {
        this.labelsSignal.set(data);
        this.loadingLanguage = null;
      },
      error: (err) => {
        this.loadingLanguage = null;
        console.error('Failed to load information request form labels:', err);
      }
    });
  }

  /** Section title by section key (e.g. 'basicData', 'submitter', 'discount'). */
  section(sectionKey: string): string {
    return this.labelsSignal()?.sections.find((s) => s.key === sectionKey)?.title ?? '';
  }

  /** Field label by section + field key (e.g. field('basicData', 'topic')). */
  field(sectionKey: string, fieldKey: string): string {
    const section = this.labelsSignal()?.sections.find((s) => s.key === sectionKey);
    return section?.fields.find((f) => f.key === fieldKey)?.label ?? '';
  }

  /** RequesterForOtherPersonData field label (keys: name, surname, personalCode, authorizationNumber, ...). */
  requester(fieldKey: string): string {
    return this.labelsSignal()?.requesterFields?.[fieldKey] ?? '';
  }

  /** PayerData field label (keys: name, surname, personalCode, companyName, country, vatNumber, ...). */
  payer(fieldKey: string): string {
    return this.labelsSignal()?.payerFields?.[fieldKey] ?? '';
  }

  /** DiscountGroupData field label (keys: name, surname, personalCode, documentNumber, reason, ...). */
  discount(fieldKey: string): string {
    return this.labelsSignal()?.discountFields?.[fieldKey] ?? '';
  }

  /** Localized enum value text; '' if missing/not loaded. */
  enumValue(family: string, value: string | null | undefined): string {
    if (!value) return '';
    return this.labelsSignal()?.enumValues?.[family]?.[value] ?? '';
  }

  /**
   * LNA-1927: how a person block renders its name — 'Combined' (one field) or 'Separate' (two fields).
   * The backend owns this decision (shared with the BO PDF); defaults to 'Separate' until labels load.
   */
  nameMode(block: string): NameDisplayMode {
    return this.labelsSignal()?.nameDisplay.modes[block] === 'Combined' ? 'Combined' : 'Separate';
  }

  /** LNA-1927: name field caption, owned by the backend nameDisplay labels; '' if not loaded. */
  nameLabel(kind: 'firstName' | 'lastName' | 'fullName'): string {
    return this.labelsSignal()?.nameDisplay.labels[kind] ?? '';
  }

  /** LNA-1927: combined display of a person's name — 'First Last', or '-' when both are empty. */
  combinedName(firstName: string | null | undefined, lastName: string | null | undefined): string {
    return [firstName, lastName].filter((part) => !!part).join(' ') || '-';
  }

  /**
   * LNA-1927: the name rows to render for a person block. In Combined mode a single 'fullName' row;
   * in Separate mode a firstName row and a lastName row. Empty values render as '-' (unchanged behaviour).
   */
  nameRows(block: string, firstName: string | null | undefined, lastName: string | null | undefined): NameDisplayRow[] {
    if (this.nameMode(block) === 'Combined') {
      return [{ label: this.nameLabel('fullName'), value: this.combinedName(firstName, lastName) }];
    }
    return [
      { label: this.nameLabel('firstName'), value: firstName || '-' },
      { label: this.nameLabel('lastName'), value: lastName || '-' }
    ];
  }

  get documentTitle(): string {
    return this.labelsSignal()?.documentTitle ?? '';
  }
}
